import { Input } from '@storeblocks/input';
import { type FC } from 'react';
import { NumericFormat } from 'react-number-format';
import { styled } from 'styled-components';

import { useUserLocale } from '@/i18n/hooks';

import { NumericFormatProps } from '../types/NumericFormatProps';
import { getNumberSeparators } from '../utils/getNumberSeparators';

interface Props {
  id: string;
  name: string;
  value: number;
  decimalScale?: number;
  disabled?: boolean;
  error?: string | boolean;
  suffix?: string;
  onChange: (units: number) => void;
  onBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
}

export const FundInput: FC<Props> = ({
  id,
  name,
  value,
  decimalScale = 0,
  disabled,
  error,
  suffix,
  onChange,
  onBlur,
}) => {
  const locale = useUserLocale();

  const numberSeparators = getNumberSeparators(locale);

  return (
    <NumericFormat<NumericFormatProps>
      id={id}
      name={name}
      alert={{ messageType: error ? 'error' : undefined }}
      value={value > 0 ? value : ''}
      valueIsNumericString
      thousandSeparator={numberSeparators.thousandSeparator}
      decimalSeparator={numberSeparators.decimalSeparator}
      decimalScale={decimalScale}
      allowNegative={false}
      onValueChange={(values) => {
        onChange(values.floatValue || 0);
      }}
      autoComplete="off"
      disabled={disabled}
      onBlur={onBlur}
      customInput={AmountBaseInput}
      $customSuffix={suffix ? suffix : ''}
    />
  );
};

const AmountBaseInput: FC<NumericFormatProps> = (props) => {
  return (
    <StyledInput
      id={props.id}
      value={props.value}
      disabled={props.disabled}
      onChange={props.onChange}
      onBlur={props.onBlur}
      alert={{
        messageType: props.alert?.messageType === 'error' ? 'error' : undefined,
      }}
      fluid
      {...props}
      suffix={{
        content: props.$customSuffix ? props.$customSuffix : '',
        props: {
          id: `${props.id}-input-suffix`,
        },
      }}
    />
  );
};

const StyledInput = styled(Input)`
  & input {
    text-align: right;
  }

  min-width: 184px;
`;
